<script lang="ts" context="module">
  export type IconButtonSizes = "small" | "medium" | "large";
  export type IconButtonVariants =
    | "primary"
    | "secondary"
    | "tertiary"
    | "active"
    | "community"
    | "system"
    | "theme-agnostic";
  export type ButtonHTMLType = "button" | "submit" | "reset";
</script>

<script lang="ts">
  import cx from "classnames";
  import IconButtonBase from "./internal/IconButtonBase.svelte";
  import { tooltip } from "$actions/tooltip";
  import type { IconType } from "$components/+blocks/OVIcon/OVIcon";
  import type { Placement } from "tippy.js";

  export let size: IconButtonSizes = "medium";
  export let variant: IconButtonVariants;
  export let icon: IconType;
  export let a11yLabel: string; // provide a label describing the button e.g. "open menu", this is also used as the HTML title
  export let ariaRole: "link" | undefined = undefined;
  export let disabled: boolean = false;
  export let customIconSize: string | undefined = undefined;
  export let htmlButtonType: ButtonHTMLType = "button";
  export let showTooltip = true;
  export let tooltipPosition: Placement = "auto";

  const variants = {
    primary: cx(
      "bg-black/5 hover:bg-black/10 text-neutral-500 hover:text-neutral-600 border border-black/10", // light mode styles
      "dark:bg-white/5 dark:hover:bg-white/10 dark:text-neutral-300 dark:hover:text-neutral-300 dark:border-white/5" // dark mode styles
    ),

    secondary: cx(
      "hover:bg-black/5 text-neutral-500 hover:text-neutral-600 border border-black/10", // light mode styles
      "dark:hover:bg-white/10 dark:text-neutral-300 dark:hover:text-neutral-300 dark:border-white/10" // dark mode styles
    ),

    tertiary: cx(
      "hover:bg-black/5 text-neutral-500/85 hover:text-neutral-600/90", // light mode styles
      "dark:hover:bg-white/10 dark:text-neutral-400/85 dark:hover:text-neutral-300/90" // dark mode styles
    ),

    // used for buttons that are theme agnostic, e.g. the "Close" icon button in the image lightbox
    "theme-agnostic":
      "bg-neutral-700/75 border-[0.5px] border-neutral-500 backdrop-blur-md text-neutral-100 hover:brightness-[1.3] brightness-105 hover:backdrop-contrast-150",

    // OTHER VARIANTS
    community:
      "bg-community-primary hover:bg-community-primary-variant text-community-text-primary",

    system: "bg-brand-blurple hover:bg-brand-blurple-hover text-neutral-100",

    active: cx(
      "bg-black/10 text-neutral-500/90", // light mode styles
      "dark:bg-white/10 dark:text-neutral-300/90" // dark mode styles
    ),
  };

  const sizes = {
    small: "h-[20x] w-[20px] rounded after:rounded",
    medium: "h-[28px] w-[28px] rounded-[5px] after:rounded-[5px]",
    large: "h-[36px] w-[36px] rounded-lg after:rounded-md",
  };

  $: sharedStyles = cx(
    "transition-[background,text]",
    "ov-focus duration-100",
    "aspect-square flex items-center justify-center",
    disabled && "opacity-25 cursor-not-allowed pointer-events-none"
  );
</script>

<span use:tooltip={{ content: showTooltip ? a11yLabel : "", placement: tooltipPosition }}>
  <IconButtonBase
    {icon}
    {disabled}
    {customIconSize}
    {a11yLabel}
    {htmlButtonType}
    {ariaRole}
    class={cx(variants[variant], sizes[size], sharedStyles)}
    on:click
  />
</span>
